// src/components/alertesCritiques/AlertesCritiques.jsx
// Le FOURNISSEUR des alertes critiques, monté une fois dans
// AdministrationLayout : il couvre tout /administration/* et rien d'autre (ni
// la connexion, ni le kiosque, ni les portails).
//
// Il porte la seule instance de l'ordonnanceur (useAlertesCritiques) : deux
// instances se disputeraient le verrou navigator.locks et doubleraient les
// appels. Il rend :
//   - `children` (la coquille : barre du haut avec le bouton « Alertes »,
//     panneau latéral, pages), qui lisent le contexte par
//     useAlertesCritiquesContexte() ;
//   - le panneau central (PanneauAlertesCritiques), qui met en page ce que le
//     serveur a composé ; rien tant qu'aucune réponse n'est arrivée.
//
// Composant séparé qui reçoit `children` : quand son état change (relecture,
// pause, compte), seuls les lecteurs du contexte se rendent à nouveau, jamais
// la coquille ni la page en cours. La coquille ne suit que l'ouverture du
// panneau latéral (onPanneauOuvertChange), pour pousser la page et replier
// le menu.
//
// Props :
//   - autreFenetreOuverte : un tiroir de la coquille (cloche, messagerie) est
//     ouvert ; le panneau central attend qu'il se ferme ;
//   - actif : false en année CLÔTURÉE (rien ne tourne, ni bouton ni panneau :
//     les liens ouvriraient l'archive et toute écriture serait refusée, 423) ;
//   - onPanneauOuvertChange(ouvert) : la coquille suit l'état du panneau
//     latéral (appelé au montage puis à chaque changement). Son état initial
//     se prend de panneauLateralMemorise() (useAlertesCritiques.js), pour ne
//     pas pousser la page avec un temps de retard après un F5.

import React, { createContext, useContext } from 'react';
import PanneauAlertesCritiques from './PanneauAlertesCritiques';
import useAlertesCritiques from './useAlertesCritiques';

const sansEffet = () => {};
const sansRelecture = async () => false;

// Hors du fournisseur (année clôturée sans fournisseur, essais d'un composant
// seul) : rien d'affiché, aucune action.
const CONTEXTE_INERTE = Object.freeze({
  actif: false,
  boutonVisible: false,
  compteBouton: null,
  panneauOuvert: false,
  ouvrirPanneau: sansEffet,
  fermerPanneau: sansEffet,
  basculerPanneau: sansEffet,
  donnees: null,
  groupes: [],
  avancement: {
    traitees: 0, reglees: 0, constatees: 0, envoyees: 0, restent: null, total: null, toutTraite: false, aucune: false
  },
  enCours: new Set(),
  intervalleMinutes: 30,
  sonActif: true,
  grandEcran: false,
  lectureEnCours: false,
  premiereLecture: false,
  luLe: null,
  erreurLecture: false,
  relirePanneau: sansRelecture,
  actualiser: sansRelecture,
  corriger: sansEffet,
  voirListe: sansEffet,
  retirerLigne: sansEffet,
  signalerConstatOuvert: sansEffet
});

const ContexteAlertesCritiques = createContext(CONTEXTE_INERTE);

/** Le contexte des alertes critiques : bouton « Alertes », panneau latéral. */
export function useAlertesCritiquesContexte() {
  return useContext(ContexteAlertesCritiques);
}

export default function AlertesCritiques({
  autreFenetreOuverte = false,
  actif = true,
  onPanneauOuvertChange = null,
  children = null
}) {
  const {
    ouvert,
    donnees,
    intervalleMinutes,
    fermer,
    retirerLigne,
    actualiser,
    pontActif,
    pontVersPanneauLateral,
    contexte
  } = useAlertesCritiques({ autreFenetreOuverte, actif, onPanneauOuvertChange });

  return (
    <ContexteAlertesCritiques.Provider value={contexte}>
      {children}
      {actif && donnees && (
        <PanneauAlertesCritiques
          ouvert={ouvert}
          donnees={donnees}
          intervalleMinutes={intervalleMinutes}
          onFermer={fermer}
          onLigneConstatee={retirerLigne}
          onActualiser={actualiser}
          pontActif={pontActif}
          onPont={pontVersPanneauLateral}
        />
      )}
    </ContexteAlertesCritiques.Provider>
  );
}
